// FILE: client/src/screens/admin/AdminSubcategoryFormScreen.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";

import Uploader from "../../components/images/Uploader";
import { CATALOGS, isValidCatalogKey } from "../../constants/shopMeta";

import { adminListCategories } from "../../actions/categoryActions";
import {
  adminCreateSubcategory,
  adminGetSubcategory,
  adminUpdateSubcategory,
  resetAdminSubcategoryCreate,
  resetAdminSubcategoryDetails,
  resetAdminSubcategoryUpdate,
} from "../../actions/subcategoryActions";
import CreateCategoryModal from "../../components/modals/CreateCategoryModal";
import { getApiErrorMessage } from "../../utils/errorMessages";

const DEFAULT_SHOP_KEY = CATALOGS?.[0]?.shopKey || "haberdashery";

const normalizeImages = (images = []) => {
  const raw = Array.isArray(images) ? images : images ? [images] : [];

  return raw
    .map((image) => {
      if (!image) return null;

      if (typeof image === "string") {
        const url = image.trim();

        if (!url) return null;

        return {
          url,
          public_id: "",
          alt: "",
          caption: "",
        };
      }

      const url = String(image.url || image.secure_url || "").trim();

      if (!url) return null;

      return {
        url,
        public_id: String(image.public_id || "").trim(),
        alt: String(image.alt || "").trim(),
        caption: String(image.caption || "").trim(),
      };
    })
    .filter(Boolean);
};

const getEntityId = (entity) => {
  if (!entity) return "";

  if (typeof entity === "string") return entity;

  return String(entity._id || entity.id || "");
};

const getParentId = (parentId) => getEntityId(parentId);

const getErrorMessage = (error) =>
  getApiErrorMessage(error, {
    fallback:
      "We could not save this subcategory right now. Please try again later.",
  });

const getInitialShopKey = (search, routeShopKey = "") => {
  const searchParams = new URLSearchParams(search);
  const queryShopKey = searchParams.get("shopKey");

  if (isValidCatalogKey(queryShopKey)) return queryShopKey.trim();
  if (isValidCatalogKey(routeShopKey)) return routeShopKey.trim();
  return DEFAULT_SHOP_KEY;
};

const sortByName = (items = []) =>
  [...items].sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || "")),
  );

const AdminSubcategoryFormScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const subcategoryId = params.id || params.subcategoryId;
  const isEditMode = Boolean(subcategoryId);

  const initialShopKey = useMemo(
    () => getInitialShopKey(location.search, params.shopKey),
    [location.search, params.shopKey],
  );

  const [shopKey, setShopKey] = useState(initialShopKey);
  const [parentId, setParentId] = useState("");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [images, setImages] = useState([]);
  const [isPublished, setIsPublished] = useState(true);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEditMode);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);

  const hasCategories = categories.length > 0;
  const canOpenCreateCategory =
    !saving && !loadingCategories && isValidCatalogKey(shopKey);

  useEffect(() => {
    dispatch(resetAdminSubcategoryCreate());
    dispatch(resetAdminSubcategoryUpdate());

    return () => {
      dispatch(resetAdminSubcategoryDetails());
    };
  }, [dispatch]);

  useEffect(() => {
    let isActive = true;

    const fetchCategories = async () => {
      const safeShopKey = String(shopKey || "").trim();

      if (!isValidCatalogKey(safeShopKey)) {
        setCategories([]);
        return;
      }

      setLoadingCategories(true);

      try {
        const data = await dispatch(
          adminListCategories({
            shopKey: safeShopKey,
            pageNumber: 1,
            pageSize: 200,
          }),
        );

        if (!isActive) return;

        const nextCategories = Array.isArray(data?.categories)
          ? data.categories
          : [];

        setCategories(sortByName(nextCategories));
      } catch (err) {
        if (!isActive) return;

        const message = getErrorMessage(err);

        setCategories([]);
        toast.error(message);
      } finally {
        if (isActive) {
          setLoadingCategories(false);
        }
      }
    };

    fetchCategories();

    return () => {
      isActive = false;
    };
  }, [dispatch, shopKey]);

  useEffect(() => {
    if (!isEditMode) return;

    let isActive = true;

    const fetchSubcategory = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await dispatch(adminGetSubcategory(subcategoryId));

        if (!isActive) return;

        const nextShopKey =
          data?.shopKey || data?.parentId?.shopKey || initialShopKey;

        setShopKey(
          isValidCatalogKey(nextShopKey)
            ? String(nextShopKey).trim()
            : initialShopKey,
        );
        setParentId(getParentId(data?.parentId));
        setName(data?.name || "");
        setSlug(data?.slug || "");
        setImages(normalizeImages(data?.images));
        setIsPublished(data?.isPublished !== false);
      } catch (err) {
        if (!isActive) return;

        const message = getErrorMessage(err);

        setError(message);
        toast.error(message);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchSubcategory();

    return () => {
      isActive = false;
    };
  }, [dispatch, initialShopKey, isEditMode, subcategoryId]);

  const handleShopKeyChange = (nextShopKey) => {
    setShopKey(nextShopKey);
    setParentId("");
    setCategories([]);
  };

  const handleCategoryCreated = (createdCategory) => {
    const createdId = getEntityId(createdCategory);

    if (!createdId) {
      toast.warning("Category was created, but its id was not returned.");
      return;
    }

    setCategories((prev) => {
      const withoutDuplicate = prev.filter(
        (category) => getEntityId(category) !== createdId,
      );

      return sortByName([...withoutDuplicate, createdCategory]);
    });

    setParentId(createdId);
  };

  const submitHandler = async (event) => {
    event.preventDefault();

    const safeShopKey = String(shopKey || "").trim();
    const safeParentId = String(parentId || "").trim();
    const safeName = String(name || "").trim();

    if (!isValidCatalogKey(safeShopKey)) {
      toast.error("Please select a valid catalogue.");
      return;
    }

    if (!safeParentId) {
      toast.error("Parent category is required.");
      return;
    }

    if (!safeName) {
      toast.error("Subcategory name is required.");
      return;
    }

    const payload = {
      shopKey: safeShopKey,
      parentId: safeParentId,
      name: safeName,
      slug: String(slug || "").trim(),
      images: normalizeImages(images),
      isPublished,
    };

    setSaving(true);
    setError("");

    try {
      if (isEditMode) {
        await dispatch(adminUpdateSubcategory(subcategoryId, payload));
        toast.success("Subcategory updated.");
      } else {
        await dispatch(adminCreateSubcategory(payload));
        toast.success("Subcategory created.");
      }

      navigate(-1);
    } catch (err) {
      const message = getErrorMessage(err);

      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Container fluid>
        <Row className="justify-content-center">
          <Col lg={9} xl={8}>
            <Card className="shadow-sm">
              <Card.Header>
                <h1 className="h4 mb-0">
                  {isEditMode ? "Edit Subcategory" : "Create Subcategory"}
                </h1>
              </Card.Header>

              <Card.Body>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <Form onSubmit={submitHandler}>
                    {error ? <Alert variant="danger">{error}</Alert> : null}

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="shopKey">
                          <Form.Label>Catalogue</Form.Label>

                          <Form.Select
                            value={shopKey}
                            onChange={(event) =>
                              handleShopKeyChange(event.target.value)
                            }
                            required
                            disabled={isEditMode || saving}
                          >
                            {CATALOGS.map((shop) => (
                              <option key={shop.shopKey} value={shop.shopKey}>
                                {shop.name}
                              </option>
                            ))}
                          </Form.Select>

                          {isEditMode ? (
                            <Form.Text className="text-muted">
                              Catalogue cannot be changed after subcategory creation.
                            </Form.Text>
                          ) : null}
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="isPublished">
                          <Form.Label>Status</Form.Label>

                          <Form.Select
                            value={isPublished ? "true" : "false"}
                            onChange={(event) =>
                              setIsPublished(event.target.value === "true")
                            }
                            disabled={saving}
                          >
                            <option value="true">Published</option>
                            <option value="false">Draft</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="parentId">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Form.Label className="mb-0">
                          Parent Category
                        </Form.Label>

                        <Button
                          type="button"
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setShowCreateCategoryModal(true)}
                          disabled={!canOpenCreateCategory}
                        >
                          New category
                        </Button>
                      </div>

                      <Form.Select
                        value={parentId}
                        onChange={(event) => setParentId(event.target.value)}
                        required
                        disabled={saving || loadingCategories}
                      >
                        <option value="">
                          {loadingCategories
                            ? "Loading categories..."
                            : hasCategories
                              ? "Select parent category"
                              : "No categories available for this shop"}
                        </option>

                        {categories.map((category) => {
                          const categoryId = getEntityId(category);

                          return (
                            <option key={categoryId} value={categoryId}>
                              {category.name}
                              {category.isPublished === false ? " (Draft)" : ""}
                            </option>
                          );
                        })}
                      </Form.Select>

                      {!loadingCategories && !hasCategories ? (
                        <Form.Text className="text-danger">
                          Create a category for this shop before saving the
                          subcategory.
                        </Form.Text>
                      ) : null}
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="name">
                      <Form.Label>Subcategory Name</Form.Label>

                      <Form.Control
                        type="text"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="Enter subcategory name"
                        required
                        disabled={saving}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="slug">
                      <Form.Label>Slug</Form.Label>

                      <Form.Control
                        type="text"
                        value={slug}
                        onChange={(event) => setSlug(event.target.value)}
                        placeholder="Leave blank to auto-generate"
                        disabled={saving}
                      />

                      <Form.Text className="text-muted">
                        Leave blank to generate from the subcategory name.
                      </Form.Text>
                    </Form.Group>

                    <Card className="mb-4">
                      <Card.Header>
                        <strong>Subcategory Images</strong>
                      </Card.Header>

                      <Card.Body>
                        <Uploader
                          existingImages={images}
                          setImageUrls={setImages}
                          folder={`subcategories/${shopKey || "uncategorized"}`}
                        />

                        <Form.Text className="text-muted d-block mt-2">
                          Upload images first, then save the subcategory. The
                          first image is treated as the cover image.
                        </Form.Text>
                      </Card.Body>
                    </Card>

                    <div className="d-flex gap-2">
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={saving || loadingCategories || !parentId}
                      >
                        {saving ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            Saving...
                          </>
                        ) : isEditMode ? (
                          "Update Subcategory"
                        ) : (
                          "Create Subcategory"
                        )}
                      </Button>

                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={() => navigate(-1)}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <CreateCategoryModal
        show={showCreateCategoryModal}
        onHide={() => setShowCreateCategoryModal(false)}
        shopKey={shopKey}
        onCreated={handleCategoryCreated}
      />
    </>
  );
};

export default AdminSubcategoryFormScreen;
